import type { ReactNode } from 'react';
import type { ProductLine } from '@popcart/core/v1';
import { Segmented } from '../../ui/bits';
import { HUB_HOST, LINE_OPTIONS, SLUG_MAX, slugPreview, type DraftField, type ShopDraft } from './lib.js';
import { PinPicker } from './PinPicker';
import '../zones/zones.css';
import './shops.css';

type Errors = Partial<Record<DraftField, string>>;

/** One labelled input with its help and error lines. */
function TextField({
  id,
  label,
  value,
  onChange,
  error,
  help,
  prefix,
  suffix,
  type = 'text',
  inputMode,
  maxLength,
  full,
  multiline,
  placeholder,
}: {
  id: string;
  label: string;
  value: string;
  onChange(v: string): void;
  error?: string;
  help?: ReactNode;
  prefix?: string;
  suffix?: string;
  type?: 'text' | 'email' | 'tel';
  inputMode?: 'text' | 'decimal' | 'numeric' | 'email' | 'tel';
  maxLength?: number;
  full?: boolean;
  multiline?: boolean;
  placeholder?: string;
}) {
  const describedBy = [help ? `${id}-help` : null, error ? `${id}-err` : null].filter(Boolean).join(' ') || undefined;
  const common = {
    id,
    value,
    maxLength,
    placeholder,
    autoComplete: 'off',
    'aria-invalid': error ? true : undefined,
    'aria-describedby': describedBy,
  } as const;
  return (
    <div className={full ? 'field full' : 'field'}>
      <label htmlFor={id}>{label}</label>
      {multiline ? (
        <textarea {...common} className="input" rows={3} onChange={(e) => onChange(e.target.value)} />
      ) : prefix || suffix ? (
        <div className="input-affix" data-invalid={error ? 'true' : undefined}>
          {prefix ? (
            <span aria-hidden="true" className="t-mono">
              {prefix}
            </span>
          ) : null}
          <input {...common} type={type} inputMode={inputMode} onChange={(e) => onChange(e.target.value)} />
          {suffix ? (
            <span aria-hidden="true" className="t-mono muted">
              {suffix}
            </span>
          ) : null}
        </div>
      ) : (
        <input {...common} className="input" type={type} inputMode={inputMode} onChange={(e) => onChange(e.target.value)} />
      )}
      {help ? (
        <span id={`${id}-help`} className="help">
          {help}
        </span>
      ) : null}
      {error ? (
        <span id={`${id}-err`} className="pill st-error err-pill" style={{ whiteSpace: 'normal' }}>
          {error}
        </span>
      ) : null}
    </div>
  );
}

/**
 * The shop record as a form: profile, money and timing, the vetting record, and (when adding) the
 * owner to invite. Used by Add shop and by the shop page.
 */
export function ShopForm({ draft, errors, set, mode }: { draft: ShopDraft; errors: Errors; set<K extends keyof ShopDraft>(k: K, v: ShopDraft[K]): void; mode: 'create' | 'edit' }) {
  const p = mode === 'create' ? 'ns' : 'es';
  const toggleLine = (l: ProductLine) => set('lines', draft.lines.includes(l) ? draft.lines.filter((x) => x !== l) : [...draft.lines, l]);
  const slug = slugPreview(draft.name);
  return (
    <>
      <section className="card dv-card" aria-labelledby={`${p}-profile-h`}>
        <div className="dv-card-h">
          <h2 id={`${p}-profile-h`} title="What customers and riders see: the name, what it sells and where riders pick up.">Shop profile</h2>
        </div>
        <div className="sh-form-grid">
          <TextField id={`${p}-name`} label="Shop name" value={draft.name} onChange={(v) => set('name', v)} error={errors.name} maxLength={80} />
          <TextField
            id={`${p}-slug`}
            label="Web address"
            prefix={`${HUB_HOST}/shop/`}
            value={draft.slug}
            onChange={(v) => set('slug', v.toLowerCase())}
            error={errors.slug}
            maxLength={SLUG_MAX}
            placeholder={mode === 'create' ? slug || 'aling-nena' : undefined}
            help={
              mode === 'create'
                ? slug
                  ? <>Leave it blank to use <b className="t-mono">{slug}</b>. Renaming the shop later won’t change it.</>
                  : 'Short and readable, like aling-nena. Renaming the shop later won’t change it.'
                : 'a–z, 0–9, hyphens · the old address keeps working'
            }
          />
          <div className="field">
            <span className="flabel">Type</span>
            <Segmented<ShopDraft['kind']>
              label="Type"
              value={draft.kind}
              options={[
                { value: 'partner', label: 'Partner' },
                { value: 'first_party', label: 'First-party' },
              ]}
              onChange={(v) => set('kind', v)}
            />
            <span className="help">{draft.kind === 'partner' ? 'Vetted seller · verify before going live' : 'PopCart’s own · no commission'}</span>
          </div>
          <fieldset className="field full" aria-describedby={errors.lines ? `${p}-lines-err` : undefined} style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="flabel t-label" style={{ fontWeight: 700 }}>
              Sells
            </legend>
            <div className="sh-lines">
              {LINE_OPTIONS.map((o) => (
                <label key={o.value} className="check-label">
                  <input type="checkbox" className="check" checked={draft.lines.includes(o.value)} onChange={() => toggleLine(o.value)} />
                  <span className="t-label">{o.label}</span>
                </label>
              ))}
            </div>
            {errors.lines ? (
              <span id={`${p}-lines-err`} className="pill st-error err-pill" style={{ whiteSpace: 'normal' }}>
                {errors.lines}
              </span>
            ) : null}
          </fieldset>
          <TextField id={`${p}-address`} label="Pickup address" value={draft.addressText} onChange={(v) => set('addressText', v)} error={errors.addressText} maxLength={200} full help="Street, barangay, city." />
          <div className="full">
            <PinPicker id={`${p}-pin`} value={draft.location} onChange={(v) => set('location', v)} error={errors.location} />
          </div>
          <TextField id={`${p}-phone`} label="Shop phone (optional)" type="tel" inputMode="tel" value={draft.phone} onChange={(v) => set('phone', v)} error={errors.phone} maxLength={20} help="Riders call on arrival" />
          <TextField id={`${p}-contact`} label="Contact email (optional)" type="email" inputMode="email" value={draft.contactEmail} onChange={(v) => set('contactEmail', v)} error={errors.contactEmail} maxLength={254} />
        </div>
      </section>

      <section className="card dv-card" aria-labelledby={`${p}-money-h`}>
        <div className="dv-card-h">
          <h2 id={`${p}-money-h`} title="Commission is a share of item sales. Prep time sets when riders get booked.">Commission and orders</h2>
        </div>
        <div className="sh-form-grid">
          <TextField id={`${p}-commission`} label="Commission" suffix="%" inputMode="decimal" value={draft.commissionPct} onChange={(v) => set('commissionPct', v)} error={errors.commissionPct} help="0 to 50" />
          <TextField id={`${p}-prep`} label="Usual prep time" suffix="min" inputMode="numeric" value={draft.prepMinutes} onChange={(v) => set('prepMinutes', v)} error={errors.prepMinutes} help="5 to 120" />
          <TextField id={`${p}-minorder`} label="Minimum order" prefix="₱" inputMode="decimal" value={draft.minOrderPesos} onChange={(v) => set('minOrderPesos', v)} error={errors.minOrderPesos} help="0 = none · up to ₱5,000" />
        </div>
      </section>

      <section className="card dv-card" aria-labelledby={`${p}-vet-h`}>
        <div className="dv-card-h">
          <h2 id={`${p}-vet-h`} title="The seller’s papers, checked offline (DTI/SEC, TIN, permits, a visit). We keep this record for every partner.">Vetting record</h2>
        </div>
        <div className="sh-form-grid">
          <TextField id={`${p}-legal`} label="Registered business name" value={draft.legalName} onChange={(v) => set('legalName', v)} error={errors.legalName} maxLength={160} help="As on the DTI/SEC certificate" />
          <TextField id={`${p}-reg`} label="DTI/SEC registration no." value={draft.registrationNo} onChange={(v) => set('registrationNo', v)} error={errors.registrationNo} maxLength={40} />
          <TextField id={`${p}-tin`} label="TIN" inputMode="numeric" value={draft.tin} onChange={(v) => set('tin', v)} error={errors.tin} maxLength={20} help="123-456-789-00000" />
          <TextField id={`${p}-notes`} label="Ops notes (optional)" value={draft.opsNotes} onChange={(v) => set('opsNotes', v)} error={errors.opsNotes} maxLength={2000} full multiline help="Only Ops sees this" />
        </div>
      </section>

      {mode === 'create' ? (
        <section className="card dv-card" aria-labelledby="ns-owner-h">
          <div className="dv-card-h">
            <h2 id="ns-owner-h" title="We email them a link to the Partner Hub. You can invite more owners later.">Shop owner</h2>
          </div>
          <TextField id="ns-owner" label="Owner’s email (optional)" type="email" inputMode="email" value={draft.ownerEmail} onChange={(v) => set('ownerEmail', v)} error={errors.ownerEmail} maxLength={254} />
        </section>
      ) : null}
    </>
  );
}
